import { Terminal } from "lucide-react";

import { IgConsoleLog } from "@intelligems/ig-theme/IgConsoleLog";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

import {
  WorkspaceConsoleGroup,
  WorkspaceEditorConsoleState,
} from "../../../types";
import { LogEntryLine } from "../../LogEntryLine";

/**
 * Bottom-of-editor console drawer. Surfaces the output captured by the latest
 * `Run tests` gates, grouped by required/generated/user source, in a dark
 * monospace scroll body. Closes via the header dismiss button.
 */
export const EditorConsole = ({
  console: editorConsole,
}: {
  console: WorkspaceEditorConsoleState;
}) => {
  const theme = useTheme();
  const hasOutput =
    editorConsole.requiredLogs.length > 0 ||
    editorConsole.generatedLogs.length > 0 ||
    editorConsole.userLogs.length > 0;

  return (
    <Stack
      sx={{
        minHeight: 0,
        maxHeight: 220,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: 1.5,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          minHeight: 32,
          px: 2,
          borderBottom: `1px solid ${theme.igBorders.core.default}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Terminal size={16} color={theme.palette.text.accent} />
          <IgTypography variant="body2" fontWeight={700}>
            Console
          </IgTypography>
        </Stack>
        <IgIconButton
          iconType="close"
          aria-label="Close console"
          onClick={editorConsole.close}
        />
      </Stack>

      <Stack
        sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 1.5 }}
        spacing={1.5}
      >
        {hasOutput ? (
          <>
            {editorConsole.requiredLogs.length > 0 ? (
              <ConsoleLogGroup
                title="Required tests"
                groups={editorConsole.requiredLogs}
              />
            ) : null}
            {editorConsole.generatedLogs.length > 0 ? (
              <ConsoleLogGroup
                title="Generated tests"
                groups={editorConsole.generatedLogs}
              />
            ) : null}
            {editorConsole.userLogs.length > 0 ? (
              <ConsoleLogGroup
                title="User tests"
                groups={editorConsole.userLogs}
              />
            ) : null}
          </>
        ) : (
          <IgConsoleLog>No console output.</IgConsoleLog>
        )}
      </Stack>
    </Stack>
  );
};

/**
 * Console output for one suite (Generated/User) in a dark monospace block,
 * sub-grouped by the test that emitted each batch of lines. Output not tied to a
 * test (top-level/import code or `beforeAll`/`afterAll` hooks) renders under a
 * "Setup" sub-header.
 */
const ConsoleLogGroup = ({
  title,
  groups,
}: {
  title: string;
  groups: WorkspaceConsoleGroup[];
}) => (
  <Stack spacing={0.5}>
    <IgTypography variant="body2" fontWeight={700}>
      {title}
    </IgTypography>
    <IgConsoleLog spacing={1.5}>
      {groups.map((group, groupIndex) => (
        <Stack key={groupIndex}>
          {/* eslint-disable-next-line ig-fe-requirements/no-hardcoded-hex-colors -- no close ig-theme match */}
          <span style={{ color: "#6a9955", fontWeight: 700 }}>
            {`› ${group.testName ?? "Setup"}`}
          </span>
          {group.logs.map((log, logIndex) => (
            <LogEntryLine key={logIndex} log={log} />
          ))}
        </Stack>
      ))}
    </IgConsoleLog>
  </Stack>
);
